import {
    AppState,
    AuthenticationState,
    THEWEST_ALL,
} from '@news-mono/web-common'
import { useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
    BreachContext,
    BreachScreenContext,
} from '../../../../web-common/src/context-providers/BreachContextProvider'
import { BreachWallContentType } from './components/BreachNotificationWrapper/BreachNotificationWrapper'
import { SubscriptionPackageProps } from './components/SubscriptionPackage/SubscriptionPackage'

type SubscriptionPackage = Pick<
    SubscriptionPackageProps,
    | 'packageTheme'
    | 'packagePath'
    | 'pretitle'
    | 'heading'
    | 'price'
    | 'originalPrice'
    | 'priceQualifier'
    | 'packageType'
    | 'ribbonTitle'
    | 'offerCode'
    | 'cancellationMessage'
    | 'googleSKU'
>

export type SubscriptionPackages = SubscriptionPackage[]

export const BASE_50_DEAL_OFFERCODE = 'DIGDIS21'

const base50percentDiscountPackage: SubscriptionPackage[] = [
    {
        packageTheme: 'red',
        packagePath: 'subscribe/new',
        pretitle: 'Digital',
        heading: 'Everyday Digital',
        price: '50c per day',
        originalPrice: '$1 per day',
        priceQualifier: 'for 8 weeks*',
        packageType: 'digital',
        ribbonTitle: '50% off',
        offerCode: BASE_50_DEAL_OFFERCODE,
        cancellationMessage: 'Cancel anytime. Min cost $14.',
    },
    {
        pretitle: 'Digital & Print',
        heading: 'Weekend Papers + Everyday Digital',
        price: '$9 per week',
        packagePath: 'subscribenow/sat-sun+digital',
        packageType: 'digitalprint',
        cancellationMessage: 'Cancel anytime. Min cost $36.',
    },
]

export const breachPackages: Record<
    BreachWallContentType,
    SubscriptionPackage[]
> = {
    default: [
        {
            packagePath: 'subscribe/new',
            pretitle: 'Digital',
            heading: 'Everyday Digital',
            price: '$1 per day',
            packageType: 'digital',
            cancellationMessage: 'Cancel anytime. Min cost $28.',
            googleSKU: 'everyday_digital_4weeks',
        },
        {
            ribbonTitle: 'Most popular',
            pretitle: 'Digital & Print',
            heading: 'Weekend Papers + Everyday Digital',
            price: '$9 per week',
            packagePath: 'subscribenow/sat-sun+digital',
            packageType: 'digitalprint',
            cancellationMessage: 'Cancel anytime. Min cost $36.',
        },
    ],
    'black-friday': [
        {
            packagePath: 'offercheck/thewest',
            offerCode: 'BLKFRI1',
            pretitle: 'Digital',
            originalPrice: '$1 a day',
            heading: 'Everyday Digital',
            price: '50c per day',
            priceQualifier: 'for 8 weeks*',
            packageType: 'digital',
        },
        {
            ribbonTitle: 'Most popular',
            pretitle: 'Digital & Print',
            heading: 'Weekend Papers + Everyday Digital',
            originalPrice: '$9 per week',
            price: '$4.50 per week',
            priceQualifier: 'for 8 weeks*',
            packagePath: 'offercheck/thewest',
            offerCode: 'BLKFRI2',
            packageType: 'digitalprint',
        },
    ],
    '50-off-breach-discount-coviddig': base50percentDiscountPackage,
    '50-off-breach-discount-govan50': base50percentDiscountPackage,
    '50-off-breach-discount': base50percentDiscountPackage,

    sport: [],
}

export function packageOfferDetails(
    type: BreachWallContentType,
): SubscriptionPackages {
    const packages: SubscriptionPackages = []

    switch (type) {
        case '50-off-breach-discount-coviddig':
        case '50-off-breach-discount-govan50':
        case '50-off-breach-discount':
            breachPackages['50-off-breach-discount'].forEach((pack) => {
                packages.push(pack)
            })
            break

        default:
            breachPackages[type].forEach((pack) => {
                packages.push(pack)
            })
            break
    }

    return packages
}

export function useUpdateBreachWallContext({
    publication,
    breachCreativeName,
    breachDeal,
    leadDepth,
    offerCode,
    packagePath,
    requiredAccessLevel,
}: BreachContext) {
    const breachScreenContext = useContext(BreachScreenContext)

    const authentication = useSelector<AppState, AuthenticationState>(
        (state) => state.authentication,
    )

    const { entitlements, isLoggedIn } = authentication

    // a returning user is a user who has registered, logged in, and has not completed a purchase
    const returningUser = isLoggedIn && !entitlements?.includes(THEWEST_ALL)

    useEffect(() => {
        breachScreenContext.setContext({
            publication,
            returningUser,
            breachCreativeName,
            breachDeal,
            leadDepth,
            offerCode,
            packagePath,
            requiredAccessLevel,
        })
        return () => {
            breachScreenContext.clearContext()
        }

        // We can ignore the breachScreenContext dependency because the setContext and clearContext functions aren't going to change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        returningUser,
        publication,
        breachCreativeName,
        breachDeal,
        leadDepth,
        offerCode,
        packagePath,
        requiredAccessLevel,
    ])
}
